import React from 'react';
import {ArrowDown} from 'lucide-react';
import { translations } from '../translations';

interface HeroProps {
  currentLang: 'de' | 'en' | 'es';
}

export const Hero: React.FC<HeroProps> = ({ currentLang }) => {
  const t = translations[currentLang].hero;
  const [isVisible, setIsVisible] = React.useState(false);

  const heroImage = 'https://static.lumi.new/47/474433a2f5ed9ba6eb192c3c8684e658.webp';

  React.useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Split headline into white part + gold accent part (mirrors the poster CI)
  const headline = {
    de: { white: 'Private Pflege & Betreuung für', gold: 'internationale Familien auf Mallorca' },
    en: { white: 'Private Care & Support for', gold: 'international families in Mallorca' },
    es: { white: 'Cuidado y atención privada para', gold: 'familias internacionales en Mallorca' },
  }[currentLang];

  return (
    <section
      id="hero"
      className="relative h-screen min-h-[100svh] md:min-h-[700px] flex flex-col overflow-hidden"
      itemScope
      itemType="https://schema.org/WPHeader"
    >
      {/* Background Image - subtle slow Ken Burns motion */}
      <div
        className="absolute inset-0 bg-cover bg-top hero-kenburns"
        style={{ backgroundImage: `url(${heroImage})`, backgroundPosition: 'top center' }}
      />

      {/* Warm golden light glow from top-left (like sunlight in the poster) */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(166,139,91,0.55),transparent_55%)]" />

      {/* Dark elegant base for text legibility on the left */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

      {/* Content - left-aligned, layered over warm light (poster layout) */}
      <div
        className={`relative z-10 w-full px-6 md:pl-12 lg:pl-16 mt-auto pb-10 md:pb-14 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-2xl">
          <h1
            className="font-serif leading-[1.1] tracking-tight font-light drop-shadow-2xl mb-5"
            itemProp="headline"
          >
            <span className="block text-white text-4xl md:text-5xl lg:text-6xl">{headline.white}</span>
            <span className="block text-[#d4b483] text-2xl md:text-3xl lg:text-4xl mt-2">{headline.gold}</span>
          </h1>

          {/* Signature gold divider line (the markante poster element) */}
          <div className="h-px w-24 bg-gradient-to-r from-[#A68B5B] to-[#A68B5B]/0 mb-8" />

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 animate-fade-in-up delay-300">
            <button
              onClick={() => scrollToSection('contact')}
              className="group relative bg-[#A68B5B] text-white px-7 py-3 md:px-8 md:py-3.5 rounded-full text-sm md:text-base font-medium overflow-hidden transition-all duration-500 shadow-xl hover:shadow-[#A68B5B]/40 transform hover:scale-105 hover:-translate-y-1"
            >
              <span className="relative z-10">{t.cta1}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#b89968] to-[#A68B5B] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="group relative bg-white/5 backdrop-blur-md text-white px-7 py-3 md:px-8 md:py-3.5 rounded-full text-sm md:text-base font-medium transition-all duration-500 shadow-lg hover:shadow-xl border border-white/40 hover:border-[#A68B5B] hover:bg-[#A68B5B]/15 transform hover:scale-105 hover:-translate-y-1"
            >
              <span className="relative z-10">{t.cta2}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce-slow">
        <div
          className="flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors cursor-pointer"
          onClick={() => scrollToSection('about')}
        >
          <span className="text-xs uppercase tracking-wider">{t.scroll}</span>
          <ArrowDown size={24} className="animate-pulse" />
        </div>
      </div>
    </section>
  );
};
